import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { resolveAppCrashDashboardHref } from 'v5/features/core/app-crash/formatAppCrashErrorLog';
import { FullPageDashboardLink } from 'v5/patterns/page-layout/FullPageDashboardLink';
import { FullPageStatusLayout } from 'v5/patterns/page-layout/FullPageStatusLayout';
import { WithServices } from 'v5/platform/ts/services';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';

export type NotFoundScreenProps = WithServices<[WithSupportService]> & {
  className?: string;
  title?: string;
  description?: string;
};

export function NotFoundScreen({
  services,
  className,
  title,
  description
}: NotFoundScreenProps) {
  const { t } = useTranslation('core', { keyPrefix: 'not_found' });
  const dashboardHref = resolveAppCrashDashboardHref();
  const openSupport = promiseState.useMutation(() =>
    services.supportService.show()
  );
  const isOpening = mt.isInProgress(openSupport.state);

  return (
    <FullPageStatusLayout
      idPrefix="not-found"
      className={className}
      title={title ?? t('title')}
      description={description ?? t('description')}
      actions={
        <>
          <Button
            type="button"
            variant="primary"
            size="md"
            visuallyDisabled={isOpening}
            aria-busy={isOpening}
            onClick={isOpening ? undefined : () => openSupport.track(undefined)}
          >
            {isOpening && <LoadingSpinner className="mr-2.5" />}
            {t('contactSupport')}
          </Button>
          <FullPageDashboardLink href={dashboardHref ?? '/'}>
            {t('goToDashboard')}
          </FullPageDashboardLink>
        </>
      }
    />
  );
}
